// The Models view of Inference (ui-spec §5.10): the on-demand catalog in 1 to 3 columns,
// filtered by model type, search and capability.
// A card opens the playground (playground.tsx) with that model selected.
import { ArrowRight } from "lucide-react";
import { useId } from "react";
import { Checkbox } from "@nodus/ui";
import { modelPublisher } from "./model-publisher";
import "./model-publisher.css";
import { LabLogo } from "./lab-logo";
import { apiLabel, CAPABILITIES, CAPABILITY_LABEL, CAPABILITY_FILTERS, MODEL_TYPES, MODEL_TYPE_LABEL, cardPriceLabel, modalityLabel, modelTitle, tokenCountLabel, type Capability, type CatalogModel, type ModelType } from "./model";

/** The model's capabilities as plain text, "Vision · Tools · Reasoning". */
function capabilityText(model: CatalogModel): string {
  return CAPABILITIES.filter((c) => model.capabilities?.[c])
    .map((c) => CAPABILITY_LABEL[c])
    .join(" · ");
}

/** Native radios provide one model type at a time, including keyboard navigation. */
export function ModelTypeFilter({ value, onChange }: { value: ModelType; onChange: (type: ModelType) => void }) {
  const name = useId();
  return <fieldset className="mb-4 min-w-0">
    <legend className="sr-only">Model type</legend>
    <div className="inline-flex max-w-full flex-wrap gap-1 rounded-lg bg-subtle p-1">
      {MODEL_TYPES.map((type) => <label key={type} className="relative cursor-pointer">
        <input type="radio" name={name} value={type} checked={value === type} onChange={() => onChange(type)} className="peer absolute inset-0 size-full cursor-pointer opacity-0" />
        <span className="flex min-h-9 items-center rounded-md px-4 text-small font-medium text-fg-muted transition-colors hover:text-fg-primary peer-checked:bg-fg-primary peer-checked:text-canvas peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-fg-primary">{MODEL_TYPE_LABEL[type]}</span>
      </label>)}
    </div>
  </fieldset>;
}

/** Capabilities refine a type; every checked feature is required. */
export function CapabilityFilter({ value, onChange }: { value: Capability[]; onChange: (caps: Capability[]) => void }) {
  return <fieldset className="flex flex-wrap items-center gap-x-5 gap-y-2 text-small">
    <legend className="sr-only">Required capabilities</legend>
    <span className="text-fg-muted" aria-hidden>Must support</span>
    {CAPABILITY_FILTERS.map((c) => <label key={c} className="inline-flex cursor-pointer items-center gap-2 text-fg-secondary">
      <Checkbox checked={value.includes(c)} onCheckedChange={(checked) => onChange(checked ? [...value, c] : value.filter((x) => x !== c))} />
      {CAPABILITY_LABEL[c]}
    </label>)}
  </fieldset>;
}

/** Modality and API stay readable without repeating bright badges on every card. */
function Chips({ model }: { model: CatalogModel }) {
  return (
    <ul aria-label="Modality and API" className="flex min-w-0 flex-wrap gap-x-3 gap-y-1 text-small text-fg-muted">
      <li>{modalityLabel(model)}</li>
      <li>{apiLabel(model)}</li>
      {model.preview ? <li className="font-medium text-fg-secondary">Preview</li> : null}
    </ul>
  );
}

function ModelCard({ model, onTry }: { model: CatalogModel; onTry: () => void }) {
  const title = modelTitle(model);
  const price = cardPriceLabel(model);
  const caps = capabilityText(model);
  const publisher = modelPublisher(model.id);
  return (
    <li style={publisher.style} className="publisher-card relative flex min-w-0 flex-col rounded-lg border border-line bg-card p-4 transition-colors hover:border-line-strong hover:bg-hover">
      <div className="flex items-center gap-3">
        <LabLogo id={model.id} className="publisher-logo size-8" />
        <div className="min-w-0 flex-1">
          <span className="publisher-label">{publisher.label}</span>
          <h3 className="break-words text-[16px] leading-6 font-semibold tracking-[var(--nd-tracking-display)] text-fg-primary">{title}</h3>
        </div>
      </div>
      <code title={model.id} className="mt-2 block truncate font-mono text-small text-fg-muted">{model.id}</code>
      <div className="mt-3">
        <Chips model={model} />
      </div>
      <p className="mt-2 min-h-4 text-small text-fg-muted">{caps}</p>
      <div className="flex-1" />
      <div className="mt-3 border-t border-line-subtle pt-3">
        <p className="text-small leading-5 text-fg-secondary tabular-nums">{price ?? "Pricing not reported"}</p>
        <div className="mt-2 flex items-center justify-between gap-3 text-small">
          <span className="text-fg-muted">{model.context_window ? `${tokenCountLabel(model.context_window)} context` : ""}</span>
          <span className="inline-flex items-center gap-1.5 text-fg-primary" aria-hidden>Open in Playground <ArrowRight className="size-3.5" /></span>
        </div>
      </div>
      <button type="button" onClick={onTry} aria-label={`Try ${title}`} className="absolute inset-0 cursor-pointer rounded-lg" />
    </li>
  );
}

/** The on-demand catalog: 1 to 3 columns by the container's width (not the viewport's, so the sidebar's state counts). */
export function ModelGrid({ models, onTry }: { models: CatalogModel[]; onTry: (id: string) => void }) {
  return (
    <div className="@container">
      <ul aria-label="Models" data-focus-inset className="grid grid-cols-1 gap-4 @min-[34rem]:grid-cols-2 @min-[56rem]:grid-cols-3">
        {models.map((m) => (
          <ModelCard key={m.id} model={m} onTry={() => onTry(m.id)} />
        ))}
      </ul>
    </div>
  );
}
